import { Button, DialogActions } from "@mui/material";
import { Form, Formik } from "formik";
import { ReactElement } from "react";
import * as Yup from "yup";

import {
  GcsImportIntegration,
  CreateGcsImportIntegrationInput,
  UpdateGcsImportIntegrationInput,
} from "~frontend/__generated__/graphql";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import FormikErrorBanner, {
  submitErrorField,
} from "~frontend/components/forms/FormikErrorBanner";
import { FormikSubmitButton } from "~frontend/components/forms/SubmitButton";
import {
  CREATE_GCS_IMPORT_INTEGRATION,
  UPDATE_GCS_IMPORT_INTEGRATION,
} from "~frontend/graphql/integration";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useMutation from "~frontend/util/useMutation";

const creationValidationSchema = Yup.object({
  name: Yup.string().required("Required"),
  bucketName: Yup.string().required("Required"),
  credentialsJSON: Yup.string().required("Required"),
});

const updateValidationSchema = Yup.object({
  name: Yup.string().required("Required"),
  bucketName: Yup.string().required("Required"),
  credentialsJSON: Yup.string(),
});

type Values = {
  name: string;
  bucketName: string;
  credentialsJSON: string;
};

function creationIntegrationValues(
  subaccountId: string,
  values: Values
): CreateGcsImportIntegrationInput {
  const { name, bucketName, credentialsJSON } = values;
  return {
    subaccountId,
    name,
    bucketName,
    credentialsJSON,
  };
}

function updateIntegrationValues(
  integrationId: string,
  values: Values
): UpdateGcsImportIntegrationInput {
  const { name, bucketName, credentialsJSON } = values;
  return {
    integrationId,
    name,
    bucketName,
    ...(credentialsJSON ? { credentialsJSON } : {}),
  };
}

export function GCSImportIntegrationSettingsForm({
  integration,
  subaccountId,
  afterCreate,
  onClose,
}: {
  integration: GcsImportIntegration | null;
  subaccountId: string;
  afterCreate?: () => void;
  onClose: () => void;
}): ReactElement {
  const editing = !!integration;

  const [createGcsImportIntegration] = useMutation(
    CREATE_GCS_IMPORT_INTEGRATION,
    {
      refetchQueries: ["ListIntegrations"],
      awaitRefetchQueries: true,
    }
  );

  const [updateGcsImportIntegration] = useMutation(
    UPDATE_GCS_IMPORT_INTEGRATION,
    {
      refetchQueries: ["ListIntegrations"],
      awaitRefetchQueries: true,
    }
  );

  async function createIntegration(values: CreateGcsImportIntegrationInput) {
    const { errors } = await createGcsImportIntegration({
      variables: { input: values },
    });

    if (errors) {
      throw errors;
    }
    if (afterCreate) {
      afterCreate();
    }

    onClose();
  }

  async function updateIntegration(values: UpdateGcsImportIntegrationInput) {
    const { errors } = await updateGcsImportIntegration({
      variables: { input: values },
    });

    if (errors) {
      throw errors;
    }

    onClose();
  }

  const getErrorMessage = useErrorMessageFormatter();
  const initialValues: Values = {
    name: integration?.name || "",
    bucketName: integration?.settings?.bucketName || "",
    credentialsJSON: "",
  };

  return (
    <Formik
      initialValues={{
        [submitErrorField]: null,
        ...initialValues,
      }}
      validationSchema={
        editing ? updateValidationSchema : creationValidationSchema
      }
      onSubmit={async (values, formik) => {
        try {
          if (editing) {
            await updateIntegration(
              updateIntegrationValues(integration.id, values)
            );
          } else {
            await createIntegration(
              creationIntegrationValues(subaccountId, values)
            );
          }
          formik.resetForm();
        } catch (e) {
          formik.setErrors({
            [submitErrorField]: getErrorMessage(e),
          });
        }
      }}
    >
      {({ isSubmitting }) => {
        return (
          <Form>
            <FormikErrorBanner />
            <FormikTextField
              name="name"
              label="Name Your Integration"
              disabled={isSubmitting}
              fullWidth
            />
            <FormikTextField
              name="bucketName"
              label="GCS Bucket Name"
              disabled={isSubmitting}
              fullWidth
            />
            <FormikTextField
              fullWidth
              multiline
              name="credentialsJSON"
              label={
                editing
                  ? "Credentials JSON (leave blank to keep current)"
                  : "Credentials JSON"
              }
              inputProps={{
                // blur text: https://stackoverflow.com/a/66618206
                style: {
                  color: "transparent",
                  textShadow: "0 0 8px rgba(0,0,0,0.5)",
                },
              }}
              rows={4}
            />
            <DialogActions>
              <Button
                disabled={isSubmitting}
                onClick={() => onClose()}
                color="secondary"
              >
                Cancel
              </Button>
              <FormikSubmitButton
                color="primary"
                variant="contained"
                submitText={editing ? "Save" : "Create"}
                loadingText={editing ? "Saving..." : "Creating..."}
              />
            </DialogActions>
          </Form>
        );
      }}
    </Formik>
  );
}
